import { memo, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ReactComponent as BulbIcon } from "../../../../assets/content-overview-images/bulb.svg";
import { ContentOverviewBlock } from "../../../../components/content-overview-block/content-overview-block";
import "./content-flow.css";
import { FlowDiagram } from "../../../../components/flow-diagram/flow-diagram";

import { ReactComponent as CategoriesIcon } from "../../../../assets/content-overview-images/categories.svg";
import { ReactComponent as DailyTasksIcon } from "../../../../assets/content-overview-images/dailyTasks.svg";
import { ReactComponent as ArticlesIcon } from "../../../../assets/content-overview-images/articles.svg";
import { ReactComponent as FactsIcon } from "../../../../assets/content-overview-images/facts.svg";
import { ReactComponent as LifePointsIcon } from "../../../../assets/content-overview-images/lifePoints.svg";
import { ReactComponent as ScenariosIcon } from "../../../../assets/content-overview-images/scenarios.svg";
import { ReactComponent as RulesIcon } from "../../../../assets/content-overview-images/rules.svg";
import { ReactComponent as PlansIcon } from "../../../../assets/content-overview-images/plans.svg";
import { ReactComponent as TagsIcon } from "../../../../assets/content-overview-images/tags.svg";

export const ContentFlow = memo(({ defaultOpen, onToggle }) => {
  const { projectCode } = useParams();

  const data = useMemo(() => {
    const nodes = [
      { id: "categories", text: "Categories", href: `/${projectCode}/content/categories`, icon: CategoriesIcon },

      { id: "tasks", text: "Tasks", href: `/${projectCode}/content/tasks`, icon: DailyTasksIcon },
      { id: "articles", text: "Articles", href: `/${projectCode}/content/articles`, icon: ArticlesIcon },
      { id: "fitfacts", text: "FitFacts", href: `/${projectCode}/content/fitfacts`, icon: FactsIcon },

      { id: "lifepoints", text: "LifePoints", href: `/${projectCode}/content/lifepoints`, icon: LifePointsIcon },
      { id: "scenarios", text: "Scenarios", href: `/${projectCode}/content/scenarios`, icon: ScenariosIcon },
      { id: "onboarding", text: "Onboarding", href: `/${projectCode}/content/onboardings`, icon: ScenariosIcon },
      { id: "rules", text: "Rules", href: `/${projectCode}/content/rules`, icon: RulesIcon },
      { id: "tags", text: "Tags", href: `/${projectCode}/content/tags`, icon: TagsIcon },
      { id: "plans", text: "Plans", href: `/${projectCode}/content/plans`, icon: PlansIcon },
    ];

    const edges = [
      { from: "categories", to: "tasks" },
      { from: "categories", to: "articles" },
      { from: "categories", to: "fitfacts" },
      { from: "categories", to: "lifepoints", route: "top" },

      { from: "tasks", to: "lifepoints" },
      { from: "tasks", to: "scenarios" },
      { from: "tasks", to: "onboarding" },

      { from: "articles", to: "lifepoints" },
      { from: "articles", to: "scenarios" },
      { from: "articles", to: "onboarding" },

      { from: "fitfacts", to: "lifepoints" },
      { from: "fitfacts", to: "scenarios" },
      { from: "fitfacts", to: "onboarding" },

      { from: "scenarios", to: "plans" },
      { from: "onboarding", to: "plans" },
      { from: "fitfacts", to: "plans", kind: "rulesBracket", pointsTo: ["rules", "tags"], },
    ];

    const grid = {
      categories: { x: 0, y: 1 },

      // level 1 (x=1)
      tasks: { x: 1, y: 1 },
      articles: { x: 1, y: 2 },
      fitfacts: { x: 1, y: 3 },

      // level 2 (x=2)
      lifepoints: { x: 2, y: 0 },
      scenarios: { x: 2, y: 2 },
      onboarding: { x: 2, y: 3 },
      rules: { x: 1.8, y: 4.2 },
      tags:  { x: 2.6, y: 4.2 },
      // level 3 (x=3)
      plans: { x: 3, y: 2 },
    };

    return { nodes, edges, grid };
  }, []);

  return (
    <ContentOverviewBlock
      title="Recommended Configuration Flow"
      icon={<BulbIcon />}
      defaultOpen={defaultOpen}
      onToggle={onToggle}
    >
      <div className="content-overview-flow">
        <div className="content-overview-flow__left">
          <FlowDiagram nodes={data.nodes} edges={data.edges} grid={data.grid} />
        </div>

        <div className="content-overview-flow__right">
          <p className="content-overview-flow__paragraph">
            <strong>Categories</strong> must be defined before creating Tasks
          </p>

          <p className="content-overview-flow__paragraph">
            <strong>Tasks, Articles, and FitFacts</strong> are referenced by <strong>Scenarios</strong> and <strong>Onboarding</strong>
          </p>

          <p className="content-overview-flow__paragraph">
            <strong>Plans</strong> require configured Tasks and Scenarios to define schedules
          </p>

          <p className="content-overview-flow__paragraph">
            <strong>LifePoints</strong> configuration depends on Categories and Tasks
          </p>

          <p className="content-overview-flow__paragraph">
            <strong>Rules</strong> filter content delivery across all modules
          </p>
        </div>
      </div>
    </ContentOverviewBlock>
  );
});
